import axios from "axios";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

// Get environment-specific API URL
const API_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : "http://localhost:5000";

// Token management functions
const getToken = () => Cookies.get("_smartweld_Cookie");

// Check if token is actually expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode(token);
    const now = Date.now() / 1000; // JWT exp is in seconds
    
    if (decoded.exp && decoded.exp < now) {
      return true;
    }
    return false;
  } catch (error) {
    return true;
  }
};

// Track if we're already handling an auth error to prevent multiple redirects
let isHandlingAuthError = false;

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout for all requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add auth header to requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Skip adding Bearer token if Authorization header is already set (e.g., Basic auth for login)
    if (!config.headers.Authorization) {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle auth errors and other responses
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('[API Timeout] Request took too long');
      error.isTimeout = true;
      error.message = 'Request timed out. Please try again.';
      return Promise.reject(error);
    }

    // Handle network errors
    if (error.code === 'ERR_NETWORK' || error.message === "Network Error") {
      console.error("Network error - please check your connection");
      error.isNetworkError = true;
      error.message = 'Network error. Please check your connection.';
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors - IMPROVED LOGIC
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Prevent multiple simultaneous auth error handlers
      if (isHandlingAuthError) {
        console.log("Auth error already being handled, skipping...");
        return Promise.reject(error);
      }

      // Check if we're on the login page - don't redirect if already there
      const currentPath = window.location.pathname.toLowerCase();
      const isLoginPage = currentPath.includes('/login');
      
      if (isLoginPage) {
        console.log("401 on login page - this is expected for wrong credentials");
        return Promise.reject(error);
      }

      // Verify the token is actually expired before clearing everything
      const token = getToken();
      const sessionExpire = sessionStorage.getItem("sessionExpire");
      
      let shouldLogout = false;
      
      if (!token) {
        console.log("No token found - logging out");
        shouldLogout = true;
      } else if (isTokenExpired(token)) {
        console.log("JWT token is expired - logging out");
        shouldLogout = true;
      } else if (sessionExpire) {
        const expireTime = Number(sessionExpire);
        const now = Date.now();
        if (now >= expireTime) {
          console.log("Session timestamp expired - logging out");
          shouldLogout = true;
        }
      }

      // Only clear auth data and redirect if session is actually expired
      if (shouldLogout) {
        isHandlingAuthError = true;

        console.log("🚪 Session expired - clearing auth data and redirecting to login");

        // Clear auth data with explicit path
        Cookies.remove("_smartweld_Cookie", { path: "/" });
        Cookies.remove("_smartweld_Cookie"); // Also try without path
        sessionStorage.removeItem("_smartWeldUser");
        sessionStorage.removeItem("sessionExpire");

        // Dispatch an event to notify the app about authentication failure
        window.dispatchEvent(
          new CustomEvent("authError", {
            detail: { message: "Your session has expired. Please log in again." },
          })
        );

        // Small delay to ensure cleanup completes
        setTimeout(() => {
          isHandlingAuthError = false;
          window.location.href = "/login";
        }, 100);
      } else {
        console.warn("⚠️ Received 401 but token/session appear valid - this may indicate a backend issue");
        // Don't logout if token is still valid - might be a backend issue
        // Just reject the request
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;